import React from 'react';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {Atom, Plugins} from '@packages/systems/wfdl';
import {px, percent} from '@packages/systems/core/utils/WebflowCssSubset';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {EDITABLE_STYLE_NAMES} from '@packages/systems/commerce/constants';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';

const {SSR, EXPORT} = WebflowEnvironments;

const CommerceCartOpenLinkIcon = Atom.createRender(
  ({attributes, classes, generateAttributes}) => {
    const runtimeEnvironment = useRuntimeEnvironment();
    if (runtimeEnvironment === SSR || runtimeEnvironment === EXPORT) {
      // on the published site, we set the -webkit-named-image background image on `CommerceCartApplePayButton`
      // we don't do it here, so that we can follow apple's spec as closely as possible. we previously
      // put the style on this element, but it caused an issue with updating the height on the button
      // and the background image wouldn't resize with it, since it was in its own element
      // we're purposely not including the attributes, so that this icon **CANNOT BE STYLED**
      return <div />;
    } else {
      // in the designer/preview, we use an SVG, so that the apple pay icon is shown across any platform
      // this SVG is a direct extraction of the -webkit-named-image from the PassKit framework on a Mac
      // which ensures this SVG will look the same as the published site's image
      return (
        <svg
          {...generateAttributes(attributes, classes)}
          viewBox="0 0 140.21 57.56"
        >
          <g transform="matrix(1.33333 0 0 -1.33333 0 57.562533)">
            <path
              d="M19.4028 37.6045c1.198 1.499 2.011 3.511 1.797 5.567-1.754-.087-3.894-1.157-5.133-2.656-1.113-1.285-2.098-3.381-1.841-5.351 1.969-.171 3.936.983 5.177 2.44M21.1772 34.7793c-2.859.17-5.29-1.623-6.656-1.623-1.366 0-3.457 1.537-5.719 1.496-2.943-.043-5.674-1.708-7.168-4.355-3.072-5.295-.811-13.15 2.177-17.463 1.451-2.134 3.199-4.483 5.503-4.399 2.177.086 3.03 1.41 5.676 1.41 2.645 0 3.414-1.41 5.718-1.367 2.389.043 3.884 2.135 5.334 4.271 1.665 2.432 2.346 4.78 2.389 4.91-.043.042-4.608 1.794-4.65 7.045-.043 4.397 3.584 6.489 3.755 6.618-2.048 3.03-5.249 3.371-6.359 3.457M42.6499 23.8164h5.705c4.328 0 6.792 2.33 6.792 6.37s-2.464 6.348-6.77 6.348h-5.727zm7.037 16.913c6.214 0 10.542-4.284 10.542-10.52 0-6.26-4.416-10.566-10.698-10.566h-6.881V8.7014h-4.972v32.028zM76.5547 17.4014v1.799l-5.526-.356c-3.108-.2-4.728-1.354-4.728-3.374 0-1.953 1.688-3.218 4.328-3.218 3.374 0 5.926 2.153 5.926 5.149m-15.093-2.064c0 4.106 3.13 6.459 8.901 6.814l6.192.378v1.775c0 2.597-1.709 4.018-4.749 4.018-2.509 0-4.328-1.288-4.706-3.263h-4.483c.133 4.151 4.04 7.169 9.322 7.169 5.682 0 9.388-2.974 9.388-7.591V8.7014h-4.594v3.84h-.111c-1.309-2.509-4.195-4.084-7.325-4.084-4.616 0-7.835 2.752-7.835 6.88M85.5508.1338v3.839c.312-.043 1.065-.088 1.465-.088 2.198 0 3.44.932 4.195 3.329l.444 1.421-8.412 23.305h5.194l5.859-18.91h.111l5.86 18.91h5.061l-8.723-24.482c-1.997-5.615-4.284-7.458-9.122-7.458-.378 0-1.599.045-1.932.134"
              fill="#FFFFFF"
            />
          </g>
        </svg>
      );
    }
  }
);

const lockedStyles = EDITABLE_STYLE_NAMES.reduce<Record<string, any>>(
  (obj, styleName) => {
    obj[styleName] = {
      when: true,
      message:
        "The Apple Pay Icon cannot be styled, due to Apple's Human Interface Guidelines.",
    };
    return obj;
  },
  {}
);

const config = createIntegratedAtom({
  displayName: 'Icon',
  showEmpty: false,
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Star')),
  },
  constraints: {
    ancestors: [{is: 'CommerceCartApplePayButton', rule: 'ExactlyOne'}],
  },
  // this errors flow due to `border` and `textColumns` not being part of `WebflowCssProperties`,
  // but we need to set those for the styles panel to disable the sections

  lockedStyles,
  style: {
    base: {
      main: {
        noPseudo: {
          // the width is set to 100% here as there was an issue in chrome where the SVG's height
          // wouldn't update when the button's height changed unless the width was explictly set
          width: percent(100),
          height: percent(50),
          minHeight: px(20),
        },
      },
    },
  },
  render: CommerceCartOpenLinkIcon,
  fields: {},
});

export default config;
